import { useState, useEffect } from 'react';

interface SystemStats {
  totalReceivers: number;
  totalFiles: number;
  storageUsed: string;
  todayUploads: number;
  activeUsers: number;
  recentActivity: Activity[];
}

interface Activity {
  type: string;
  description: string;
  timestamp: string;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<SystemStats>({
    totalReceivers: 0,
    totalFiles: 0,
    storageUsed: '0 GB',
    todayUploads: 0,
    activeUsers: 0,
    recentActivity: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchStats();
    // Refresh every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    setRefreshing(true);
    try {
      const response = await fetch('https://fastfiles-api.b-9f2.workers.dev/api/admin/stats');
      const data = await response.json();

      if (data.success) {
        setStats(data.stats);
      } else {
        // Use demo data if API not ready
        setStats({
          totalReceivers: 42,
          totalFiles: 156,
          storageUsed: '2.3 GB',
          todayUploads: 23,
          activeUsers: 8,
          recentActivity: [
            { type: 'upload', description: 'user@example.com uploaded 3 files', timestamp: '5 mins ago' },
            { type: 'create', description: 'New receiver created: Project Files', timestamp: '12 mins ago' },
            { type: 'download', description: '5 files downloaded from Wedding Photos', timestamp: '1 hour ago' }
          ]
        });
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      // Use demo data
      setStats({
        totalReceivers: 42,
        totalFiles: 156,
        storageUsed: '2.3 GB',
        todayUploads: 23,
        activeUsers: 8,
        recentActivity: [
          { type: 'upload', description: 'user@example.com uploaded 3 files', timestamp: '5 mins ago' },
          { type: 'create', description: 'New receiver created: Project Files', timestamp: '12 mins ago' },
          { type: 'download', description: '5 files downloaded from Wedding Photos', timestamp: '1 hour ago' }
        ]
      });
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  };

  const statCards = [
    {
      title: 'Total Receivers',
      value: stats.totalReceivers,
      icon: '📁',
      color: 'from-blue-500 to-blue-600',
      change: '+12%'
    },
    {
      title: 'Total Files',
      value: stats.totalFiles,
      icon: '📄',
      color: 'from-purple-500 to-purple-600',
      change: '+23%'
    },
    {
      title: 'Storage Used',
      value: stats.storageUsed,
      icon: '💾',
      color: 'from-green-500 to-green-600',
      change: '+5%'
    },
    {
      title: "Today's Uploads",
      value: stats.todayUploads,
      icon: '📤',
      color: 'from-orange-500 to-orange-600',
      change: '+18%'
    },
    {
      title: 'Active Users',
      value: stats.activeUsers,
      icon: '👥',
      color: 'from-pink-500 to-pink-600',
      change: '+8%'
    }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Admin Dashboard</h1>
          <p className="text-gray-400">System overview and statistics</p>
        </div>
        <button
          onClick={fetchStats}
          disabled={refreshing}
          className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
            refreshing
              ? 'bg-gray-700 text-gray-400'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          <span className={refreshing ? 'animate-spin' : ''}>🔄</span>
          Refresh
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
        {statCards.map((card, index) => (
          <div
            key={index}
            className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-gray-600 transition-all hover:transform hover:scale-105"
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`p-3 bg-gradient-to-br ${card.color} rounded-lg`}>
                <span className="text-2xl">{card.icon}</span>
              </div>
              <span className="text-xs text-green-400 font-medium">{card.change}</span>
            </div>
            <div className="text-2xl font-bold text-white mb-1">{card.value}</div>
            <div className="text-sm text-gray-400">{card.title}</div>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Upload Trend Chart */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h2 className="text-xl font-bold text-white mb-4">Upload Trends (7 days)</h2>
          <div className="h-64 flex items-end gap-2">
            {[40, 60, 45, 80, 65, 90, 75].map((height, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-2">
                <div
                  className="w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t-lg transition-all hover:from-blue-500 hover:to-blue-300"
                  style={{ height: `${height}%` }}
                ></div>
                <span className="text-xs text-gray-500">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i]}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Storage Distribution */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h2 className="text-xl font-bold text-white mb-4">Storage by File Type</h2>
          <div className="space-y-4">
            {[
              { type: 'Images', size: '890 MB', percent: 38, color: 'bg-blue-500' },
              { type: 'Documents', size: '567 MB', percent: 25, color: 'bg-green-500' },
              { type: 'Videos', size: '456 MB', percent: 20, color: 'bg-purple-500' },
              { type: 'Archives', size: '234 MB', percent: 10, color: 'bg-orange-500' },
              { type: 'Other', size: '153 MB', percent: 7, color: 'bg-gray-500' }
            ].map((item, i) => (
              <div key={i}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-300">{item.type}</span>
                  <span className="text-gray-400">{item.size}</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div
                    className={`${item.color} h-2 rounded-full transition-all`}
                    style={{ width: `${item.percent}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <h2 className="text-xl font-bold text-white mb-4">Recent Activity</h2>
        <div className="space-y-3">
          {stats.recentActivity.map((activity, i) => (
            <div key={i} className="flex items-start gap-3 p-3 bg-gray-700/50 rounded-lg">
              <span className="text-2xl">
                {activity.type === 'upload' ? '📤' : activity.type === 'create' ? '✨' : '📥'}
              </span>
              <div className="flex-1">
                <p className="text-gray-200">{activity.description}</p>
                <p className="text-xs text-gray-500 mt-1">{activity.timestamp}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <button className="p-4 bg-gray-800 hover:bg-gray-700 rounded-xl border border-gray-700 text-left transition-colors">
          <span className="text-2xl mb-2 block">📊</span>
          <span className="text-white font-medium">Export Reports</span>
        </button>
        <button className="p-4 bg-gray-800 hover:bg-gray-700 rounded-xl border border-gray-700 text-left transition-colors">
          <span className="text-2xl mb-2 block">🗑️</span>
          <span className="text-white font-medium">Clean Storage</span>
        </button>
        <button className="p-4 bg-gray-800 hover:bg-gray-700 rounded-xl border border-gray-700 text-left transition-colors">
          <span className="text-2xl mb-2 block">📧</span>
          <span className="text-white font-medium">Send Notifications</span>
        </button>
        <button className="p-4 bg-gray-800 hover:bg-gray-700 rounded-xl border border-gray-700 text-left transition-colors">
          <span className="text-2xl mb-2 block">⚙️</span>
          <span className="text-white font-medium">System Settings</span>
        </button>
      </div>
    </div>
  );
}